import React, { useState } from "react";
import { wishlistAtom } from "../../store/atoms";
import Products from "./Products";
import Ordered from "./Ordered";
import { useRecoilState } from "recoil";
import { useIsMobile } from '../../customHook/useIsMobile';

const scrollbarStyles = `
  .sidebar-scroll::-webkit-scrollbar {
    width: 0px;
    background: transparent;
  }
  .sidebar-scroll {
    -ms-overflow-style: none;  /* IE and Edge */
    scrollbar-width: none;      /* Firefox */
  }
`;

const HeartIcon = ({ section, card }) => {
  const [wishlist, setWishlist] = useRecoilState(wishlistAtom);
  const isSelected = wishlist[section]?.some((c) => c.title === card.title);

  const handleClick = (e) => {
    e.stopPropagation();
    setWishlist((prev) => {
      const existing = prev[section] || [];
      if (isSelected) {
        return {
          ...prev,
          [section]: existing.filter((c) => c.title !== card.title),
        };
      } else {
        return { ...prev, [section]: [...existing, card] };
      }
    });
  };

  return (
    <div
      onClick={handleClick}
      style={{
        position: "absolute",
        top: "8px",
        right: "8px",
        width: "16px",
        height: "16px",
        cursor: "pointer",
        zIndex: 2,
      }}
    >
      {isSelected ? (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="13"
          viewBox="0 0 16 13"
          fill="#FFF"
        >
          <path
            d="M6.94307 11.645C5.06222 10.2385 1.33594 7.02304 1.33594 4.12946C1.33594 2.21692 2.73944 0.666504 4.66927 0.666504C5.66927 0.666504 6.66927 0.999837 8.0026 2.33317C9.33594 0.999837 10.3359 0.666504 11.3359 0.666504C13.2657 0.666504 14.6693 2.21692 14.6693 4.12946C14.6693 7.02304 10.943 10.2385 9.06214 11.645C8.4292 12.1182 7.576 12.1182 6.94307 11.645Z"
            stroke="#FFF"
            strokeWidth="1"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ) : (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="13"
          viewBox="0 0 16 13"
          fill="none"
        >
          <path
            d="M6.94307 11.645C5.06222 10.2385 1.33594 7.02304 1.33594 4.12946C1.33594 2.21692 2.73944 0.666504 4.66927 0.666504C5.66927 0.666504 6.66927 0.999837 8.0026 2.33317C9.33594 0.999837 10.3359 0.666504 11.3359 0.666504C13.2657 0.666504 14.6693 2.21692 14.6693 4.12946C14.6693 7.02304 10.943 10.2385 9.06214 11.645C8.4292 12.1182 7.576 12.1182 6.94307 11.645Z"
            stroke="#F5F3ED"
            strokeWidth="1"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )}
    </div>
  );
};

const ServicesSidebar = ({ isOpen, onClose }) => {
  const [selectedCard, setSelectedCard] = useState(null);
  const [showProducts, setShowProducts] = useState(false);
  const [showOrdered, setShowOrdered] = useState(false);
  const [packagesOpen, setPackagesOpen] = useState(true);
  const [universityOpen, setUniversityOpen] = useState(false);
  const [selectedUniversity, setSelectedUniversity] = useState({
    title: "University of California, LA",
    image: "/UCLAlogo.svg",
  });

  const [universities, setUniversities] = useState([
    { title: "WashU", image: "/washulogo.png" },
    { title: "NorthWestern", image: "/northwesternlogo.png" },
    { title: "University of Florida-Gainesville", image: "/floridalogo.png" },
    { title: "University of Wisconsin-Madison", image: "/wisconsinlogo.png" },
    { title: "Penn State University", image: "/pennstatelogo.png" },
  ]);

    const mobileWidth = useIsMobile(); 

  const handleUniversitySelect = (uni) => {
    // Store the currently selected university temporarily
    const prevSelected = selectedUniversity;

    // Replace the clicked one with the previously selected one
    setSelectedUniversity(uni);

    // Swap logic: replace the clicked one in the list
    setUniversities((prevList) =>
      prevList.map((u) => (u.title === uni.title ? prevSelected : u))
    );

    setUniversityOpen(false);
  };

  const handleCardClick = (card) => {
    setSelectedCard(card);
    setShowProducts(true);
  };

  const handleBackToServices = () => {
    setSelectedCard(null);
    setShowProducts(false);
  };

  const handleSendPackage = () => {
    setShowProducts(false);
    setShowOrdered(true);
  };

  const handleGoBackToServices = () => {
    setShowProducts(false);
    setShowOrdered(false);
    setSelectedCard(null);
  };

  return (
    <>
      <style>{scrollbarStyles}</style>

      <div
        style={{
          position: "fixed",
          top: isOpen ? "90px" : "0",
          right: isOpen ? (!mobileWidth ? "20px" : "") : "-450px",
          width: !mobileWidth ? "440px" : "370px",
          height: isOpen ? "calc(100vh - 110px)" : "100vh",
          background: "rgba(255, 255, 255, 0.15)",
          backdropFilter: "blur(40px)",
          border: "none",
          boxShadow: isOpen ? "-4px 0 24px rgba(0,0,0,0.1)" : "none",
          transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
          zIndex: 1000,
          borderRadius: isOpen ? "32px" : "0",
          opacity: isOpen ? 1 : 0,
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Fixed Top Section */}
        {!showProducts && !showOrdered && (
          <div style={{ flex: '0 0 auto', padding: '24px', position: 'relative' }}>
            {/* Header */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 24,
              }}
            >
              <h2
                style={{
                  color: "#FFFFFF",
                  fontFamily: "Manrope",
                  fontSize: "28px",
                  fontWeight: 600,
                  margin: 0,
                }}
              >
                Get a Hand
              </h2>
            </div>

            {/* University Section */}
            <div style={{ color: '#FFF', fontFamily: 'Manrope', fontSize: 16, fontWeight: 700, marginBottom: 8 }}>
              University
            </div>

            {/* Selected University Card with Arrow */}
            <div
              style={{
                display: 'flex',
                padding: '12px 16px',
                justifyContent: 'space-between',
                alignItems: 'center',
                borderRadius: '20px',
                background: 'rgba(255,255,255,0.15)',
                backdropFilter: 'blur(30px)',
                width:  !mobileWidth ? "380px" : "320px",
                position: 'relative',
                cursor: 'pointer',
              }}
              onClick={() => setUniversityOpen(!universityOpen)}
            >
              {/* Left Section - Image & Title */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div
                  style={{
                    width: '60px',
                    height: '60px',
                    borderRadius: '12px',
                    background: `url(${selectedUniversity.image}) lightgray 50% / cover no-repeat`,
                  }}
                ></div>
                <div
                  style={{
                    color: '#FFF',
                    fontFamily: 'Manrope',
                    fontSize: '16px',
                    fontWeight: 600,
                  }}
                >
                  {selectedUniversity.title}
                </div>
              </div>

              {/* Right Section - Arrow */}
              <div
                style={{
                  display: 'flex',
                  width: '32px',
                  height: '32px',
                  padding: '10px',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: '10px',
                  cursor: 'pointer',
                }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="9" viewBox="0 0 14 9" fill="none">
                  <path
                    d={universityOpen ? "M13 8L7 2L1 8" : "M1 2L7 8L13 2"}
                    stroke="#F5F3ED"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
              </div>
            </div>

            {/* Dropdown below the card */}
            {universityOpen && (
              <div
                style={{
                  marginTop: "8px",
                  borderRadius: "16px",
                  background: "rgba(255,255,255,0.10)",
                  backdropFilter: "blur(25px)",
                  padding: "8px",
                  // width: "380px",
                  display: "flex",
                  flexDirection: "column",
                  gap: "8px",
                }}
              >
                {universities.map((uni) => (
                  <div
                    key={uni.title}
                    onClick={() => handleUniversitySelect(uni)}
                    style={{
                      display: "flex",
                      padding: "10px",
                      alignItems: "center",
                      gap: "12px",
                      borderRadius: "14px",
                      background: "rgba(255,255,255,0.15)",
                      backdropFilter: "blur(30px)",
                      cursor: "pointer",
                      transition: "background 0.3s",
                    }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.background =
                        "rgba(255,255,255,0.25)")
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.background =
                        "rgba(255,255,255,0.15)")
                    }
                  >
                    <div
                      style={{
                        width: "48px",
                        height: "48px",
                        borderRadius: "12px",
                        background: `url(${uni.image}) lightgray 50% / cover no-repeat`,
                      }}
                    ></div>
                    <div
                      style={{
                        color: "#FFF",
                        fontFamily: "Manrope",
                        fontSize: "15px",
                        fontWeight: 400,
                      }}
                    >
                      {uni.title}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}




        {/* Scrollable Collapsible Sections */}
        <div
          className="sidebar-scroll"
          style={{
            flex: "1 1 auto",
            overflowY: "auto",
            padding: "0 24px 24px 24px",
          }}
        >
          {showOrdered ? (
            <Ordered
              card={selectedCard}
              onGoBackToServices={handleGoBackToServices}
            />
          ) : showProducts ? (
            <Products
              card={selectedCard}
              onBack={handleBackToServices}
              onSendPackage={handleSendPackage}
            />
          ) : (
            <CollapsibleSection
              title="Services"
              sectionKey="Services"
              onCardClick={handleCardClick}
              isOpen={packagesOpen}
              toggleOpen={() => setPackagesOpen(!packagesOpen)}
              cardsCount={13}
            />
          )}
        </div>
      </div>

      {isOpen && (
        <div
          onClick={onClose}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100vh",
            background: "transparent",
          }}
        />
      )}
    </>
  );
};

const CollapsibleSection = ({
  title,
  sectionKey,
  onCardClick,
  isOpen = true,
  toggleOpen,
  cardsCount = 2,
}) => {
  const allCards = [
    {
      title: `Car Shipment`,
      subtitle: "details",
      image: "/shipment.svg",
      price: "$125.00",
    },
    {
      title: `Broken Device Repair`,
      subtitle: "details",
      image: "/repair.svg",
      price: "$75.00",
    },
    {
      title: `Airport Shuttle`,
      subtitle: "details",
      image: "/airport1.svg",
      price: "$120.00",
    },
    {
      title: `Notary Service`,
      subtitle: "details",
      image: "/notary.svg",
      price: "$50.00",
    },
    {
      title: `Event Planning`,
      subtitle: "details",
      image: "/graduation2.svg",
      price: "$65.00",
    },
    {
      title: `Pack & Move`,
      subtitle: "details",
      image: "/movein.svg",
      price: "$65.00",
    },
    {
      title: `Wardrobe Consult`,
      subtitle: "details",
      image: "/wardrobe1.svg",
      price: "$65.00",
    },
    {
      title: `Urgent Care Assistance`,
      subtitle: "details",
      image: "/sick.svg",
      price: "$65.00",
    },
    {
      title: `TV Install/Removal`,
      subtitle: "details",
      image: "/dorms.svg",
      price: "$65.00",
    },
    {
      title: `Grocery Shopping`,
      subtitle: "details",
      image: "/dorm2.svg",
      price: "$65.00",
    },
    {
      title: `Summer Storage Assistance`,
      subtitle: "details",
      image: "/sumstorage.svg",
      price: "$65.00",
    },
    {
      title: `Dorm/Apt Organization`,
      subtitle: "details",
      image: "/dormorg.svg",
      price: "$65.00",
    },
    {
      title: `Dorm Makeover`,
      subtitle: "details",
      image: "/dormdecor.svg",
      price: "$65.00",
    },
  ];
  const cards = allCards.slice(0, cardsCount);

  const ArrowIcon = () =>
    isOpen ? (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="18"
        height="10"
        viewBox="0 0 14 9"
        fill="none"
      >
        <path
          d="M13 8L7 2L1 8"
          stroke="#F5F3ED"
          strokeWidth="2"
          strokeLinecap="round"
        />
      </svg>
    ) : (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="18"
        height="10"
        viewBox="0 0 14 9"
        fill="none"
      >
        <path
          d="M1 2L7 8L13 2"
          stroke="#F5F3ED"
          strokeWidth="2"
          strokeLinecap="round"
        />
      </svg>
    );

  return (
    <div style={{ marginBottom: 24 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          cursor: "pointer",
        }}
        onClick={toggleOpen}
      >
        <div
          style={{
            color: "#FFF",
            fontFamily: "Manrope",
            fontSize: "17px",
            fontWeight: 600,
          }}
        >
          {title}
        </div>
        <div
          style={{
            width: "24px",
            height: "24px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          {/* <ArrowIcon /> */}
        </div>
      </div>

      {isOpen && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "12px",
            marginTop: "12px",
          }}
        >
          {Array.from({ length: Math.ceil(cards.length / 2) }).map(
            (_, rowIdx) => {
              const rowCards = cards.slice(rowIdx * 2, rowIdx * 2 + 2);
              const needsPlaceholder = rowCards.length < 2;

              return (
                <div
                  key={rowIdx}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                  }}
                >
                  {rowCards.map((card) => (
                    <div
                      key={card.title}
                      style={{
                        position: "relative",
                        display: "flex",
                        height: "114px",
                        padding: "12px",
                        flexDirection: "column",
                        alignItems: "flex-start",
                        gap: "4px",
                        width: "50%", // fixed width instead of flex-grow
                        borderRadius: "20px",
                        background: "rgba(255, 255, 255, 0.15)",
                        backdropFilter: "blur(30px)",
                        overflow: "hidden",
                        cursor: "pointer",
                      }}
                      onClick={() => onCardClick(card)}
                    >
                      <HeartIcon section={sectionKey} card={card} />
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          height: "87px",
                          justifyContent: "space-between",
                        }}
                      >
                        <div
                          style={{
                            color: "#FFF",
                            fontSize: window.innerWidth>=768 ? "15px" : '13px',
                            fontWeight: 500,
                          }}
                        >
                          {card.title}
                        </div>
                        <div
                          style={{
                            color: "#FFF",
                            fontSize: "13px",
                            fontWeight: 400,
                          }}
                        >
                          {card.price}
                        </div>
                      </div>
                      <img
                        src={card.image}
                        alt={card.title}
                        style={{
                           width: window.innerWidth>=768 ?"98px" : '61px',
                        height: window.innerWidth>=768 ?"65px" : '40px',
                          position: "absolute",
                          right: "1px",
                          bottom: "6px",
                        }}
                      />
                    </div>
                  ))}

                  {/* placeholder keeps layout aligned if odd number of cards */}
                  {needsPlaceholder && (
                    <div
                      style={{
                        width: "50%",
                        height: "114px",
                        borderRadius: "20px",
                        background: "transparent",
                      }}
                    />
                  )}
                </div>
              );
            }
          )}
        </div>
      )}
    </div>
  );
};

export default ServicesSidebar;
